import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { styles, colors } from './UI';

// The account manager's own phone number.
//
// It is edited HERE, by the rep, rather than in Admin -> Users, because its
// only customer-facing job is the Contact line on their quotes and needing an
// admin to type it is why most accounts had none. The write goes to the user's
// OWN row (PUT /auth/me/phone) and the result is pushed back into the auth
// context, so the topbar, the request header and the next quote all agree
// without a reload.
//
// Both surfaces below render NOTHING editable while previewing as another user
// (`?_as=`): the server refuses that write, and an input that always fails is
// worse than no input at all.

export function MyPhoneEditor({ width = 170, onSaved }) {
  const { user, setUser } = useAuth();
  const stored = user?.phone || '';
  const [value, setValue] = useState(stored);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  // Another surface (the topbar vs the request header) may have saved it —
  // follow the context rather than staying frozen at first render.
  useEffect(() => { setValue(user?.phone || ''); }, [user?.phone]);

  const dirty = value.trim() !== stored.trim();

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setError('');
    try {
      const { phone } = await api.updateMyPhone(value);
      setUser(u => (u ? { ...u, phone } : u));
      setSavedAt(new Date());
      if (onSaved) onSaved(phone);
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <input
        style={{ ...styles.input, fontSize: 12, padding: '3px 7px', width }}
        value={value}
        maxLength={40}
        placeholder="(409) 555-0134"
        title="The number printed on your quotes. Extensions are fine."
        onChange={e => { setValue(e.target.value); setError(''); setSavedAt(null); }}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
      />
      <button type="button"
        style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 9px',
          ...(dirty ? { borderColor: colors.orange, color: colors.orange, fontWeight: 700 } : {}) }}
        disabled={!dirty || saving}
        onClick={save}
      >{saving ? 'Saving…' : 'Save'}</button>
      {error && <span style={{ color: colors.red, fontSize: 11 }}>{error}</span>}
      {!error && savedAt && !dirty && (
        <span style={{ color: '#10B981', fontSize: 11, fontWeight: 600 }}>✓ Saved</span>
      )}
    </span>
  );
}

// The request header's point-of-contact line: who owns this request and how the
// customer reaches them. Read-only for anyone but the owner — a phone number is
// the owner's to give out, and an admin editing it here would write to their
// own row, not the AM's.
export function AmContactLine({ request, onPhoneSaved }) {
  const { user } = useAuth();
  const isMe = !!user && !user.is_preview && request?.created_by === user.id;
  const name = request?.created_by_name
    || (request?.external_submitter_name ? `${request.external_submitter_name} (FS PM)` : null);
  if (!name) return null;

  // The owner's own view follows the auth context, so the number updates the
  // moment it is saved from anywhere — including the topbar menu.
  const phone = isMe ? (user.phone || '') : (request?.created_by_phone || '');
  const email = isMe ? (user.email || '') : (request?.created_by_email || '');

  return (
    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, display: 'flex',
      alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span title="Account Manager on this request. This is the contact block printed on the quote.">
        👤 <strong style={{ color: '#374151' }}>{name}</strong>
        {email ? <span> · {email}</span> : null}
      </span>
      {/* The owner edits it in place; everyone else reads it. Never both — the
          input already shows the number, so printing it alongside reads as two
          different values. */}
      {!isMe && phone && <span>· {phone}</span>}
      {isMe && (
        <>
          <span style={{ color: phone ? '#6B7280' : '#B45309', fontWeight: phone ? 400 : 700 }}>
            {phone ? '· My phone' : '· Add your phone —'}
          </span>
          <MyPhoneEditor onSaved={onPhoneSaved} />
        </>
      )}
      <span style={{ color: '#9CA3AF', fontSize: 11 }}>
        (prints on the quote as the customer's point of contact)
      </span>
    </div>
  );
}
